import { scrollCellIntoView } from "_shared/_views/highlights/traverser-stop-scroll";
import type { ProposalIssue, ProposalIssuePart } from "work/proposals/issues/proposal-issues";

/** The attribute a fee schedule grid puts on a cell it has flagged with an issue (#6097). */
export const FEE_SCHEDULE_ISSUE_CELL_ATTRIBUTE = "data-fee-schedule-issue";

/** What a flagged cell is marked with: its category, and the part of the rate the issue is about. */
export function feeScheduleIssueCellKey(categoryId: string, part: ProposalIssuePart): string {
  return `${categoryId}:${part}`;
}

/** How long to wait for the grid to draw the cell and stop moving before giving up on it. */
const REVEAL_WINDOW_MS = 1500;

/**
 * Scrolls the grid to the cell an issue is about, sideways and up or down, once the layout holds
 * still (#6097).
 *
 * Pressing the issue also opens the category editor, and the page gives the panel its width out of
 * the grid's, over a transition. Revealed at once, the cell was in view in a grid about to narrow
 * under it, and pushed back off the right edge. So this waits for the cell to stay put for two frames
 * running, and only then scrolls, once. A cell that never appears in the window — on another page of
 * the grid, or a part with nothing flagged — is left alone.
 *
 * Returns a cancel, for a press that is overtaken by the next.
 */
export function revealFeeScheduleIssueCell(issue: ProposalIssue, root: ParentNode = document): () => void {
  const categoryId = issue.field.id?.value;
  if (!issue.part || !categoryId) return () => undefined;
  const selector = `[${FEE_SCHEDULE_ISSUE_CELL_ATTRIBUTE}="${feeScheduleIssueCellKey(categoryId, issue.part)}"]`;

  const started = performance.now();
  let lastPosition: string | undefined;
  let stillFrames = 0;
  let frame = requestAnimationFrame(step);

  function step(now: number) {
    const cell = root.querySelector(selector);
    if (cell) {
      const box = cell.getBoundingClientRect();
      const position = `${box.left},${box.top},${box.width}`;
      stillFrames = position === lastPosition ? stillFrames + 1 : 0;
      lastPosition = position;
      if (stillFrames >= 2) {
        scrollCellIntoView(cell);
        return;
      }
    }
    if (now - started < REVEAL_WINDOW_MS) frame = requestAnimationFrame(step);
    else if (cell) scrollCellIntoView(cell);
  }

  return () => cancelAnimationFrame(frame);
}
